import { Component, OnInit, ChangeDetectionStrategy, ViewChild, ChangeDetectorRef, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { GlobalValidators } from '../../utils/validators';
import { AccountService } from '../../services/account.service';
import { PortalProductsService } from '../../services/portal-products.service';
import { Router } from '@angular/router';
import { getEncripted } from '../../utils/pw-encripted';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-mfa-authenticator',
  templateUrl: './mfa-authenticator.component.html',
  styleUrls: ['./mfa-authenticator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfaAuthenticatorComponent implements OnInit, OnDestroy {
  mfaValidation = 'fail';
  mfaForm: FormGroup;
  currentUser;
  mfaTypes = ['PIN'];
  mfaValidationType = 'PIN';
  types = [];
  resendingVerificationCodes = false;
  mfaErrors = null;
  mfaVerifying = false;
  forgotPin = false;
  senderEmail = '';
  @ViewChild('mfaPin1Row') mfaPin1Row: any;
  @ViewChild('mfaPin2Row') mfaPin2Row: any;
  @ViewChild('mfaPin3Row') mfaPin3Row: any;
  @ViewChild('mfaPin4Row') mfaPin4Row: any;
  @Output() emitEvent = new EventEmitter<any>();
  mfaAuthenticationProcess: boolean;
  private langChangeSubscription: Subscription;

  // Helper method to get element by index
  private getElementByIndex(index: number): any {
    const elements = [this.mfaPin1Row, this.mfaPin2Row, this.mfaPin3Row, this.mfaPin4Row];
    return elements[index];
  }

  constructor(
    private fb: FormBuilder,
    private accountService: AccountService,
    private cdRef: ChangeDetectorRef,
    private portalProductService: PortalProductsService,
    private router: Router,
    public translate: TranslateService
  ) {
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.cdRef.detectChanges(); // Force change detection
    });
  }

  async ngOnInit() {
    try {
      this.currentUser = await this.accountService.currentUserAsync();
      const [localPart, domain] = this.currentUser?.email.split('@');
      // if (localPart.length < 3) return email; // In case the local part is too short to mask

      const maskedLocal = localPart.slice(0, 3) + '****' + localPart.slice(-2);
      this.senderEmail = `${maskedLocal}@${domain}`;

      if (this.currentUser) {
        if (this.currentUser?.isPinAvailable) {
          this.mfaAuthenticationProcess = true;
          this.cdRef.detectChanges();
        } else {
          this.mfaAuthenticationProcess = false;
          this.cdRef.detectChanges();
        }
        // if (this.currentUser.mfaType === 'PIN') {
        //   this.mfaValidationType = 'PIN';
        //   this.types.push('PIN');
        // } else if (this.currentUser.mfaType === 'OTP' || this.currentUser.mfaType === 'SMS' || this.currentUser.mfaType === 'EMAIL') {
        //   this.mfaValidationType = 'OTP';
        //   this.types.push('OTP');
        // }
      }
      this.cdRef.detectChanges();
      this.bindForm();
      if (this.mfaValidationType === 'OTP') {
        await this.sendVerificationCodes();
      }
      const firstElement = this.getElementByIndex(0);
      if (firstElement?.nativeElement) {
        firstElement.nativeElement.focus();
      }
    } catch (e) {
      console.log(e);
    }
  }

  async goBackToLogin() {
    await this.accountService.signOut();
    this.emitEvent.emit('fail');
    await this.router.navigate(['/signin']);
  }

  async sendVerificationCodes() {
    try {
      this.resendingVerificationCodes = true;
      this.cdRef.detectChanges();
      // await this.portalProductService.sendOTP();
      await this.accountService.sendLoginOTP(true);
      // await this.portalProductService.sendOTP();
      this.resendingVerificationCodes = false;
      this.cdRef.detectChanges();
    } catch (e) {
      this.resendingVerificationCodes = false;
      this.cdRef.detectChanges();
    }
  }

  bindForm() {
    this.mfaForm = this.fb.group({
      // mfaType: new FormControl(this.mfaValidationType)
    });
    if (this.mfaValidationType === 'PIN') {
      this.mfaForm.addControl(
        'pinCode',
        new FormGroup({
          pin1: new FormControl('', [GlobalValidators.required]),
          pin2: new FormControl('', [GlobalValidators.required]),
          pin3: new FormControl('', [GlobalValidators.required]),
          pin4: new FormControl('', [GlobalValidators.required]),
          // pin5: new FormControl('', [GlobalValidators.required]),
          // pin6: new FormControl('', [GlobalValidators.required])
        })
      );
    } else if (this.mfaValidationType === 'OTP') {
      this.mfaForm.addControl(
        'otpCode',
        new FormGroup({
          pin1: new FormControl('', GlobalValidators.required),
          pin2: new FormControl('', [GlobalValidators.required]),
          pin3: new FormControl('', [GlobalValidators.required]),
          pin4: new FormControl('', [GlobalValidators.required]),
        })
      );
    }
    // console.log(this.mfaForm);
    // this.enterPinFG.valueChanges.subscribe((val) => {
    //   this.pinMismatchError = false;
    // });
    this.cdRef.detectChanges();
  }

  mfaConfigure() {
    this.mfaAuthenticationProcess = true;
    this.cdRef.detectChanges();
    setTimeout(() => {
      const firstElement = this.getElementByIndex(0);
      if (firstElement?.nativeElement) {
        firstElement.nativeElement.focus();
      }
    }, 10);
  }

  async verifyMFA(form) {
    if (form.invalid) {
      return;
    }
    this.mfaVerifying = true;
    this.cdRef.detectChanges();
    let enteredOTP = form.value[this.mfaValidationType === 'PIN' ? 'pinCode' : 'otpCode']
      ? this.getCode(
        (this.mfaValidationType === 'PIN' && ['pin1', 'pin2', 'pin3', 'pin4']) || ['pin1', 'pin2', 'pin3', 'pin4'],
        form.value[this.mfaValidationType === 'PIN' ? 'pinCode' : 'otpCode']
      )
      : '';
    if (enteredOTP) {
      if (this.mfaValidationType === 'PIN') {
        try {
          this.mfaVerifying = true;
          this.cdRef.detectChanges();
          await this.accountService.validatePIN(getEncripted.bind(this)(enteredOTP), false);
          await this.accountService.loadCurrentUserFromServer();
          localStorage.setItem('mfaAuth', 'true');
          this.mfaVerifying = false;
          this.mfaValidation = 'success';
          this.emitEvent.emit(this.mfaValidation);

          this.cdRef.detectChanges();
        } catch (e) {
          this.mfaVerifying = false;
          form.reset();
          const firstElement = this.getElementByIndex(0);
          if (firstElement?.nativeElement) {
            firstElement.nativeElement.focus();
          }
          this.mfaErrors = e.error.message;
          this.cdRef.detectChanges();
        }
      } else if (this.mfaValidationType === 'OTP') {
        try {
          await this.portalProductService.validateOtp(enteredOTP);
          localStorage.setItem('mfaAuth', 'true');
          this.mfaVerifying = false;
          this.cdRef.detectChanges();
          this.mfaValidation = 'success';
          this.emitEvent.emit(this.mfaValidation);
        } catch (e) {
          this.mfaVerifying = false;
          this.mfaErrors = e.error.message;
          this.cdRef.detectChanges();
        }
      }
    }
    // console.log(enteredOTP);
  }

  forgotPinView() {
    this.mfaErrors = null;
    this.forgotPin = true;
    this.bindForm();
    // this.enterPinFG.reset();
    // this.errorMassage = '';
    // this.forgetPinEmitter.emit(false);
    this.cdRef.detectChanges();
  }

  newPinEnterScreenDone(event) {
    // this.forgotPin = false;
    this.cdRef.detectChanges();
  }

  forgetPinFromSetPin() {
    this.forgotPin = false;
    this.cdRef.detectChanges();
    setTimeout(() => {
      const firstElement = this.getElementByIndex(0);
      if (firstElement?.nativeElement) {
        firstElement.nativeElement.focus();
      }
    }, 10);
  }

  getCode = (pinArray = ['pin1', 'pin2', 'pin3', 'pin4'], value) => {
    // console.log(pinArray);
    let pinCode = '';
    pinArray.map((field) => {
      pinCode = pinCode + (value[field] ? value[field]?.toString() : '');
    });
    return pinCode;
  };

  restrictInput(event: KeyboardEvent, index): void {
    console.log('works');
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight'];
    // Allow navigation and editing keys
    if (allowedKeys.includes(event.key)) {
      let pos = index;
      if (event.keyCode === 8 || event.keyCode === 37) {
        const currentElement = this.getElementByIndex(index);
        if (event.keyCode === 8 && currentElement?.nativeElement?.value) {
          pos = index;
        } else {
          pos = index - 1;
        }
      } else {
        pos = index + 1;
      }
      if (pos > -1 && pos < (this.mfaValidationType === 'PIN' ? 4 : 4)) {
        const targetElement = this.getElementByIndex(pos);
        if (targetElement?.nativeElement) {
          const valueLength = targetElement.nativeElement.value.length;
          targetElement.nativeElement.setSelectionRange(valueLength, valueLength);
          targetElement.nativeElement.focus();
        }
      }
      return;
    }

    // Allow numeric input (0-9)
    if (/^[0-9]$/.test(event.key)) {
      return;
    }

    // Prevent any other key
    event.preventDefault();
  }

  // async validateInput(event: Event) {
  //   const inputElement = event.target as HTMLInputElement;
  //   inputElement.value = inputElement.value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
  // }

  // restrictInput(event): void {
  //   const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
  //   if (
  //     allowedKeys.indexOf(event.key) === -1 &&
  //     !/^[0-9]$/.test(event.key)
  //   ) {
  //     event.preventDefault();
  //   }
  // }

  blockPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  // async keyUpEvent(event, index, formGroup: string, formControl: string) {
  async validateInput(event, index, formGroup: string, formControl: string) {
    if (this.mfaErrors) {
      this.mfaErrors = '';
      this.cdRef.detectChanges();
    }
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = inputElement.value.replace(/[^0-9]/g, '');
    console.log(inputElement.value, this.mfaForm.get(formGroup).get(formControl).value, '');
    if (
      this.mfaForm.get(formGroup).get(formControl).value &&
      inputElement.value &&
      this.mfaForm.get(formGroup).get(formControl).value === inputElement.value
    ) {
      let pos = index;
      if (event.keyCode === 8 || event.keyCode === 37) {
        pos = index - 1;
      } else {
        pos = index + 1;
      }
      if (pos > -1 && pos < (this.mfaValidationType === 'PIN' ? 4 : 4)) {
        const targetElement = this.getElementByIndex(pos);
        if (targetElement?.nativeElement) {
          targetElement.nativeElement.focus();
        }
      }
      if (this.mfaForm.valid && index === (this.mfaValidationType === 'PIN' ? 3 : 3)) {
        await this.verifyMFA(this.mfaForm);
      }
    }
  }

  async ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
    if (this.mfaValidation === 'fail') {
      await this.accountService.signOut();
      this.emitEvent.emit('fail');
      await this.router.navigate(['/signin']);
    }
  }
}
